import { _DICC_ALUMNO } from "../../data/modulo/Alumno";
import { _DICC_ESCUELA } from "../../data/modulo/Escuela";
import { _DICC_GRUPO } from "../../data/modulo/Grupo";
import "../../ui/components-lit/GlobalSearch/GlobalSearch";
import { GlobalSearch } from "../../ui/components-lit/GlobalSearch/GlobalSearch";
import { SearchProvider, SearchResult } from "../../ui/components-lit/GlobalSearch/types";

const GLOBAL_SEARCH_TAG = "global-search";
const GLOBAL_SEARCH_ID = "global-search-root";
const removeDiacritics = (str: string) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, '');

export class GlobalSearchService {
    private static _instance: GlobalSearchService;
    private _component: GlobalSearch | null = null;

    private constructor() { }

    static getInstance(): GlobalSearchService {
        if (!GlobalSearchService._instance) {
            GlobalSearchService._instance = new GlobalSearchService();
        }
        return GlobalSearchService._instance;
    }

    init(): void {
        if (document.body) {
            this.mount();
            return;
        }

        document.addEventListener("DOMContentLoaded", () => this.mount(), { once: true });
    }

    refreshProviders(): void {
        if (!this._component) {
            return;
        }

        this._component.providers = this.buildDefaultProviders();
    }

    private mount(): void {
        if (this._component) {
            return;
        }

        const existing = document.getElementById(GLOBAL_SEARCH_ID);
        if (existing && existing.tagName.toLowerCase() === GLOBAL_SEARCH_TAG) {
            this._component = existing as GlobalSearch;
            this.refreshProviders();
            return;
        }

        const element = document.createElement(GLOBAL_SEARCH_TAG) as GlobalSearch;
        element.id = GLOBAL_SEARCH_ID;
        element.providers = this.buildDefaultProviders();
        document.body.appendChild(element);
        this._component = element;
    }

    private filter(query: string, idValue: string, searchValues: string[]): boolean {
        const normalizedId = idValue.trim().toLowerCase();
        const normalizedQuery = query.trim().toLowerCase();
        const valuesToSearch = [...searchValues, normalizedId, `0${normalizedId}`, `00${normalizedId}`, `${normalizedId}.0`, `0${normalizedId}.0`, `00${normalizedId}.0`];
        return valuesToSearch.some(value => removeDiacritics(value.toLocaleLowerCase()).includes(normalizedQuery));
    }

    private buildDefaultProviders(): SearchProvider[] {
        const alumnoProvider: SearchProvider = {
            key: "alumno",
            label: "Alumnos",
            search: (query: string): SearchResult[] => {
                const category = "Alumnos";

                return Array.from(_DICC_ALUMNO.values())
                    .filter((alumno) => {
                        const fullName = `${alumno.Nombre} ${alumno.ApPaterno} ${alumno.ApMaterno}`;
                        const idValue = String(alumno.IdChild);
                        return this.filter(query, idValue, [fullName, category]);
                    })
                    .map<SearchResult>((alumno) => ({
                        id: `alumno-${alumno.IdChild}`,
                        title: `${alumno.Nombre} ${alumno.ApPaterno}`.trim(),
                        subtitle: `ID ${alumno.IdChild}`,
                        category: category,
                        action: () => {
                            window.location.hash = `alumnos/alumnos/panel--id=${alumno.IdChild}&mode=All`;
                        },
                    }));
            }
        };

        const escuelaProvider: SearchProvider = {
            key: "escuela",
            label: "Escuelas",
            search: (query: string): SearchResult[] => {
                const category = "Escuelas";

                return Array.from(_DICC_ESCUELA.values())
                    .filter((escuela) => {
                        const schoolName = (escuela.Nombre || "");
                        const idValue = String(escuela.IdKinder);
                        return this.filter(query, idValue, [schoolName, category]);
                    })
                    .map<SearchResult>((escuela) => ({
                        id: `escuela-${escuela.IdKinder}`,
                        title: escuela.Nombre || `Escuela ${escuela.IdKinder}`,
                        subtitle: `ID ${escuela.IdKinder}`,
                        category: category,
                        action: () => {
                            window.location.hash = `escuelas/escuelas/panel--id=${escuela.IdKinder}`;
                        },
                    }));
            }
        };

        const grupoProvider: SearchProvider = {
            key: "grupo",
            label: "Grupos",
            search: (query: string): SearchResult[] => {
                const category = "Grupos";

                return Array.from(_DICC_GRUPO.values())
                    .filter((grupo) => {
                        const groupName = (grupo.Nombre || "");
                        const idValue = String(grupo.IdGrupo);
                        return this.filter(query, idValue, [groupName, category]);
                    })
                    .map<SearchResult>((grupo) => ({
                        id: `grupo-${grupo.IdGrupo}`,
                        title: grupo.Nombre || `Grupo ${grupo.IdGrupo}`,
                        subtitle: `ID ${grupo.IdGrupo}`,
                        category: category,
                        action: () => {
                            window.location.hash = `escuelas/grupos/panel--id=${grupo.IdGrupo}&mode=All`;
                        },
                    }));
            }
        };

        return [alumnoProvider, escuelaProvider, grupoProvider];
    }
}

export const globalSearchService = GlobalSearchService.getInstance();